import { useEffect, useState } from "react";
import { Wifi, WifiOff } from "lucide-react";
import { useIsMobile } from "../chat/useMedia";

const BACK_ONLINE_MS = 2500;

// A small pill while the device has no network ("Back online" briefly when it returns), so a page
// that won't load or save explains itself. Phones: just above the tab bar.
function OfflineBanner() {
  const mobile = useIsMobile();
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [justBack, setJustBack] = useState(false);

  useEffect(() => {
    let timer = 0;
    const goOnline = () => {
      setOnline(true);
      setJustBack(true);
      clearTimeout(timer);
      timer = setTimeout(() => setJustBack(false), BACK_ONLINE_MS);
    };
    const goOffline = () => {
      clearTimeout(timer);
      setJustBack(false);
      setOnline(false);
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (online && !justBack) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-[45] flex justify-center px-4"
      style={{ bottom: mobile ? "calc(56px + env(safe-area-inset-bottom) + 12px)" : 16 }}
    >
      <div
        className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white shadow-lg ${
          online ? "bg-green-600" : "bg-gray-900 dark:bg-gray-700"
        }`}
      >
        {online ? <Wifi size={16} aria-hidden="true" /> : <WifiOff size={16} aria-hidden="true" />}
        {online ? "Back online" : "You're offline. Some things won't load or save until you reconnect."}
      </div>
    </div>
  );
}

export default OfflineBanner;
